import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SubmitHandler } from 'react-hook-form';

import {
    domainesSelector,
    GroupeTravailUtilisateurApiObject,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateursAppDispatch,
} from '@europrocurement/l2d-domain';

import {
    DomaineJsonldUtilisateurReadDomaineRead,
    GroupesDeTravailJsonld as GroupeTravailDTO,
} from '@europrocurement/l2d-domain/openApi/ApiUtilisateur';

import { FlexyForm, FormObject, FormStructure } from '@europrocurement/flexy-form';
import { CenterCircularProgress, useModal } from '@europrocurement/flexy-components';
import { enqueueSnackbar } from 'notistack';
import { format } from 'date-fns';
import { Box } from '@mui/material';
import {
    createWorkGroupFormStructure,
    SelectDomainsOptions,
} from '../../../functions/workgroupInfosFormStructure';
import { domainesOptionsSort } from '../../../functions/domainesOptionsSort';
import { formatPhoneNumber } from '../../../functions/formatPhoneNumber';

/**
 * Types Declaration part.
 */
type ReturnedFormData = Pick<GroupeTravailUtilisateurApiObject, 'lib' | 'email' | 'phone'> & {
    domaine: SelectDomainsOptions;
    dCloture: Date | null;
    dCreation: Date | null;
};

// Domaines collection.
type Domains = DomaineJsonldUtilisateurReadDomaineRead[];

// Workgroup Update Form props.
type Props = {
    workgroup: GroupeTravailUtilisateurApiObject;
};

export const WorkGroupUpdateInfosForm: React.FunctionComponent<Props> = function ({ workgroup }) {
    const dispatch = useDispatch<UtilisateursAppDispatch>();
    const { modalActions } = useModal();

    // Get selector and thunk
    const { getDomaines, selectGroupeTravail, groupesTravailApi } = useUtilisateurService();

    // Get workgroup params to update except domaine
    const { lib, email, phone, dCloture, dCreation } = workgroup;

    /**
     * Domaines part.
     *
     * Get domaines.
     */
    const domainesDataSource = useUtilisateursServiceSelector(domainesSelector);
    const domainesLoaded = domainesDataSource.status === 'succeeded';
    const [domaines, setDomaines] = useState<Domains>();
    const [domainesReady, setDomainesReady] = useState(false);

    // On Mount: get domaines
    useEffect(() => {
        const fetchDomains = () => {
            // No pagination to get all items
            dispatch({
                type: `${domainesDataSource.slicename}/set${domainesDataSource.name}Pagination`,
                payload: { page: 0, itemsPerPage: 20 },
            });
            dispatch(getDomaines({}));
        };
        fetchDomains();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Set domaines data once loaded
    useEffect(() => {
        if (domainesLoaded) {
            setDomaines(domainesDataSource.data);
            setDomainesReady(true);
        }
    }, [domainesLoaded, domainesDataSource.data]);

    // Make domaines full list an array of valid select options for the form.
    const domainsOptionsList = domainesOptionsSort(domaines);

    /**
     * Form structure part.
     */
    // Set default values with actual workgroup data
    const defaultValues: FormObject = {
        lib,
        email,
        domaine: { label: workgroup.domaine?.ddmLibelle, value: workgroup.domaine?.['@id'] },
        phone,
        dCreation: dCreation ? new Date(dCreation) : null,
        dCloture: dCloture ? new Date(dCloture) : null,
    };

    const formStructure: FormStructure[] = createWorkGroupFormStructure(domainsOptionsList);

    /**
     * Form submit part.
     */
    // On form submit handler.
    const onSubmit: SubmitHandler<ReturnedFormData> = (data) => {
        // Get needed values
        const { iduserReferent, fDelete, dCloture: workgroupCloture } = workgroup;
        const {
            lib: dataLib,
            domaine: dataDomaine,
            phone: dataPhone,
            email: dataEmail,
            dCreation: dataDCreation,
            dCloture: dataDCloture,
        } = data;

        // Check on a inactive workgroup that cloture date is not null
        const today = new Date('now');

        let actualCloture = null;
        if (workgroupCloture) {
            actualCloture = new Date(workgroupCloture);
        }

        if (fDelete !== false || (actualCloture && actualCloture <= today)) {
            if (!dataDCloture) {
                enqueueSnackbar(
                    'Ce groupe est inactif et doit donc impérativement avoir une date de clôture',
                    {
                        variant: 'error',
                    },
                );
                return;
            }
        }

        // Check dates are valid
        if (dataDCreation && dataDCloture && dataDCloture < dataDCreation) {
            enqueueSnackbar(
                'La date de clôture ne peut pas être antérieure à la date de création',
                {
                    variant: 'error',
                },
            );
            return;
        }

        // const actualInscriptions = formatInscriptionsDTO(inscriptions);

        const updatedWorkgroup: GroupeTravailDTO = {
            idGroupeTravail: workgroup['@id'],
            lib: dataLib,
            domaine: dataDomaine.value,
            iduserReferent: iduserReferent?.['@id'],
            fDelete: workgroup.fDelete,
            phone: dataPhone?.trim() === '' ? null : formatPhoneNumber(dataPhone),
            email: dataEmail?.trim() === '' ? null : dataEmail,
            dCreation: dataDCreation ? format(dataDCreation, 'yyyy-MM-dd') : null,
            dCloture: dataDCloture ? format(dataDCloture, 'yyyy-MM-dd') : null,
            inscriptions: [],
        };

        const updateWorkGroup = () => {
            groupesTravailApi
                .apiGroupestravailIdGroupeTravailStatusPatch({
                    idGroupeTravail: `${workgroup.id}`,
                    status: 'none',
                    groupesDeTravail: updatedWorkgroup,
                })
                .then(() => {
                    // Close form modal.
                    modalActions.close();

                    // Add notification to user.
                    enqueueSnackbar('Le groupe de travail a bien été modifié.', {
                        variant: 'success',
                    });

                    // Update store.
                    dispatch(selectGroupeTravail({ id: workgroup.id as number }));
                })
                .catch((error) => {
                    console.error(error);
                    const errorMessage = error.response?.data?.exception?.message;
                    enqueueSnackbar(`Une erreur est survenue: ${errorMessage}`, {
                        variant: 'error',
                    });
                });
        };
        updateWorkGroup();
    };

    return (
        <Box sx={{ height: '50vh', maxWidth: '50vw' }}>
            {!domainesReady ? (
                <CenterCircularProgress sx={{ width: '50vw' }} />
            ) : (
                <FlexyForm
                    formObject={defaultValues}
                    formStructure={formStructure}
                    onSubmit={onSubmit}
                />
            )}
        </Box>
    );
};

export default WorkGroupUpdateInfosForm;
